import { useMutation, InfiniteData, useQueryClient } from '@tanstack/react-query';
import { MutableRefObject } from 'react';
import { contentApi, IContentListResponse, TFolder } from '@_entities/content';

import { predicateType, predicateFolder, predicateFolderIdList } from '@_shared/libs';

export function useUpdateContentsFolderMutation(
	selectedContentIds: number[],
	selectedFolderId: MutableRefObject<Record<number, number>>,
) {
	const queryClient = useQueryClient();

	// update Folder Mutation
	const { mutateAsync: updateFolder } = useMutation({
		mutationFn: ({ updateFolderId }: { updateFolderId: number; updateFolderName: string }) =>
			contentApi.updateContentListFolder(selectedContentIds, updateFolderId),
		onMutate: async ({ updateFolderId, updateFolderName }) => {
			// 해당 요청으로 온, 남아있던 요청이 있다면 취소해서 충돌 방지
			await queryClient.cancelQueries({
				predicate: (query) =>
					predicateFolder(query, 'all') ||
					predicateFolderIdList(query, Object.keys(selectedFolderId.current)) ||
					predicateFolder(query, String(updateFolderId)) ||
					predicateType(query, 'folderList'),
			});

			// 액션 전 데이터
			const previousFolderContents = queryClient.getQueriesData<
				InfiniteData<IContentListResponse>
			>({
				predicate: (query) =>
					predicateFolderIdList(query, Object.keys(selectedFolderId.current)) ||
					predicateFolder(query, 'all'),
			});

			// 액션
			queryClient.setQueriesData<InfiniteData<IContentListResponse>>(
				{
					predicate: (query) => predicateFolder(query, 'all'),
				},
				(old) => {
					if (!old) return old;

					return {
						...old,
						pages: old.pages.map((page) => ({
							...page,
							count: page.count,
							contentData: page.contentData.map((content) =>
								selectedContentIds.includes(content.id)
									? {
											...content,
											folderId: updateFolderId,
											folderName: updateFolderName,
										}
									: content,
							),
							pinnedData: page.pinnedData?.map((content) =>
								selectedContentIds.includes(content.id)
									? {
											...content,
											folderId: updateFolderId,
											folderName: updateFolderName,
										}
									: content,
							),
						})),
					};
				},
			);

			queryClient.setQueriesData<InfiniteData<IContentListResponse>>(
				{
					predicate: (query) =>
						predicateFolderIdList(query, Object.keys(selectedFolderId.current)),
				},
				(old) => {
					if (!old) return old;

					return {
						...old,
						pages: old.pages.map((page) => ({
							...page,
							count: page.count - selectedContentIds.length,
							contentData: page.contentData.filter(
								(content) => !selectedContentIds.includes(content.id),
							),
							pinnedData: page.pinnedData?.filter(
								(content) => !selectedContentIds.includes(content.id),
							),
						})),
					};
				},
			);

			// 액션: 폴더 리스트 상태에 count 조정
			queryClient.setQueriesData<TFolder[]>(
				{
					predicate: (query) => predicateType(query, 'folderList'),
				},
				(old) => {
					if (!old) return old;

					const folderIds = Object.keys(selectedFolderId.current);

					return old.map((folder) => {
						if (folder.id === updateFolderId && folder.count) {
							return {
								...folder,
								count: folder.count + selectedContentIds.length,
							};
						}

						if (folderIds.includes(folder.id.toString()) && folder.count) {
							return {
								...folder,
								count: folder.count - selectedFolderId.current[folder.id],
							};
						}

						return folder;
					});
				},
			);

			return {
				updateFolderId,
				previousFolderContents,
			};
		},
		onError: (_, __, context) => {
			context?.previousFolderContents.forEach(([queryKey, previousData]) => {
				queryClient.setQueriesData<InfiniteData<IContentListResponse>>(
					{
						queryKey,
					},
					previousData,
				);
			});
		},
		onSettled: (_, __, { updateFolderId }) => {
			queryClient.refetchQueries({
				predicate: (query) => predicateFolder(query, String(updateFolderId)),
			});

			queryClient.invalidateQueries({
				predicate: (query) =>
					predicateFolder(query, 'all') ||
					predicateType(query, 'folderList') ||
					predicateFolderIdList(query, Object.keys(selectedFolderId.current)),
				refetchType: 'none',
			});
		},
	});

	return updateFolder;
}
